import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { flushSync } from "react-dom";
import type { PublicQuote } from "@forgepoint/shared";
import type { Editor } from "@tiptap/core";
import {
  addColumnsColumn,
  appendTableColumn,
  chromeAnchorNodeId,
  duplicateBlock,
  getBlockInfo,
  insertBlockAt,
  insertTableColumn,
  mergeTableCells,
  moveTableColumn,
  insertTableRow,
  moveBlockRelative,
  moveBlockStep,
  removeBlock,
  removeTableColumn,
  removeTableRow,
  replaceTemplateInner,
  resizeColumnsColumn,
  resizeTableColumn,
  setBlockAttribute,
  setBlockClass,
  setBlockStyle,
  unmergeTableCells,
  TEXT_EDITABLE_TAGS,
  type BlockInfo,
  type BlockInsertPosition,
  type DocumentEdit,
  type MergeDirection,
  type StyleProperty,
} from "./documentEditor";
import {
  convertBoundMarkdownToHtml,
  normalizeAuthoredMarkdownBlocks,
} from "./markdownToHtml";
import {
  authoredBody,
  canEditRenderedText,
  commitInlineTextEdit,
  fragmentHoldingOffset,
  loadSessionEditorDoc,
  readSourceTextRange,
  renderedTextOffsets,
  resolveSourceEditTarget,
  sessionMarkdown,
} from "./renderedTextEdit";
import {
  editorDocToSliceMarkdown,
  markdownToEditorDoc,
  type EditorDoc,
} from "./textEditModel";
import {
  caretTextblockPoint,
  type SessionCaret,
  type TextblockPoint,
} from "./textEditCaret";
import {
  committedSpanMarkdown,
  draftBodyLength,
  editorBodyWindow,
  foldEditorMarkdown,
  openTextEditDraft,
  previewMarkdown,
  rebaseTextEditDraft,
  seededEditorMarkdown,
  viewMarkdown,
  windowShowsEditor,
  type TextEditDraft,
} from "./textEditDraft";
import type { InlineTextEditControls } from "./InlineTextEditOverlay";
import { resolveQuoteField } from "./quoteFields";
import {
  canRedo,
  canUndo,
  createHistory,
  recordEdit,
  redo as redoHistory,
  STYLE_COALESCE_WINDOW_MS,
  undo as undoHistory,
  type EditCoalescing,
  type HistorySelection,
  type HistorySnapshot,
  type HistoryState,
} from "./history";
import {
  captureSelectionPoints,
  dropHitTestX,
  type CapturedSelection,
  type HitTestColumn,
} from "./overlayGeometry";
import {
  clipAncestorBounds,
  findRenderedBlockInstances,
  isEditorChromeTarget,
  isRenderArtifact,
  renderedBlockFromEventTarget,
  renderedBlockNodeId,
} from "./renderedDom";

/** Container-relative geometry of the drag-reorder drop indicator line. */
export interface DropIndicatorRect {
  top: number;
  left: number;
  width: number;
}

/** An open inline text-edit session, rendered by InlineTextEditOverlay. */
export interface InlineTextEditSession {
  /**
   * Monotonic session id, keying the overlay so each session remounts. A
   * hand-over to another fragment keeps it: the overlay re-anchors in place.
   */
  id: number;
  /** The rendered block element the session is anchored to. */
  element: HTMLElement;
  /**
   * The editor doc parsed from the SOURCE markdown the session shows: the
   * block's body or fragment slice at open, the new fragment's draft slice
   * after a hand-over (which re-seeds the live editor with it).
   */
  initialDoc: EditorDoc;
  /**
   * Whether the session root can hold block structure: lists join the schema
   * and Enter splits into a new paragraph. False for an inline `<span>` root.
   */
  blocksEnabled: boolean;
  /**
   * The native selection the opening gesture left on the rendered block (a
   * caret for a click, a range for a click-drag), restored onto the session's
   * surface so the gesture carries over. Null falls back to a caret at the end.
   */
  initialSelection: CapturedSelection | null;
  /** The caret a hand-over restores in the re-seeded editor, or null. */
  initialCaret: TextblockPoint | null;
}

/** Pointer/mouse handlers to spread onto the preview container. */
export interface PreviewHandlers {
  onClick: (event: ReactMouseEvent<HTMLDivElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerLeave: (event: ReactPointerEvent<HTMLDivElement>) => void;
}

/** The full editor API returned by useDocumentEditor. */
export interface DocumentEditorApi {
  /** The stored document HTML - the single source of truth. */
  documentHtml: string;
  /** The quote projection bound into the document. */
  quote: PublicQuote;
  /** The HTML currently rendered in the preview. */
  renderedHtml: string;
  /** The block the pointer is over, or null when the hover is hidden. */
  hoveredNodeId: string | null;
  /**
   * The block the hover controls act on: the hovered block, hoisted to its
   * enclosing table when the pointer is on grid content. Null with the hover.
   */
  hoverChromeNodeId: string | null;
  /**
   * True when the hovered block renders on every page (repeatable header or
   * footer), so the hover ring narrows to the pointed copy the way the
   * selection ring does.
   */
  hoverOutlinesSingleInstance: boolean;
  /**
   * The rendered instance the hover last resolved to, so the handle lands on
   * the copy the pointer is actually over for a block that renders more than
   * once. Treated as a hint: check it is still an instance of the hovered
   * block before using it.
   */
  hoveredInstanceRef: RefObject<HTMLElement | null>;
  /** True once a handle drag has passed the movement threshold. */
  isDragging: boolean;
  /**
   * Starts a drag-reorder session from a block's hover drag handle - the only
   * entry point into dragging, so a pointerdown on a block's body never moves
   * it.
   */
  startBlockDrag: (
    nodeId: string,
    event: ReactPointerEvent<HTMLElement>,
  ) => void;
  /** Inspector info for the selected block, or null. */
  selection: BlockInfo | null;
  /** True while an inline text edit is in progress. */
  isTextEditing: boolean;
  /** The open inline text-edit session, or null when none is open. */
  textEdit: InlineTextEditSession | null;
  /**
   * The live TipTap instance of the open session, or null when none is open.
   * The format toolbar binds its mark and list controls to it.
   */
  activeTextEditor: Editor | null;
  /** Registers the mounted session overlay's controls (null on unmount). */
  registerTextEditControls: (controls: InlineTextEditControls | null) => void;
  /** Commits the open inline text-edit session as one document edit. */
  commitTextEdit: () => void;
  /** Cancels the open inline text-edit session, discarding changes. */
  cancelTextEdit: () => void;
  /** Schedules a preview render of the open session's unsaved draft. */
  onTextDraftChange: () => void;
  /** Hands the open session over after its anchored element was detached. */
  onTextElementDetached: () => void;
  /** Steps an emptied session back to the previous page's fragment. */
  onTextBackspaceWhenEmpty: () => boolean;
  /**
   * Converts the selected BOUND fp-markdown block into plain, inline-editable
   * HTML (the currently-resolved field value), detaching it from the quote
   * field.
   */
  convertSelectedMarkdownToAuthored: () => void;
  /** Drop indicator geometry while drag-reordering a block. */
  dropIndicator: DropIndicatorRect | null;
  /** Ref for the scrollable preview container. */
  previewRef: RefObject<HTMLDivElement>;
  /**
   * The rendered instance the selection was last established from, for canvas
   * chrome that must land on the copy the user pointed at rather than on an
   * arbitrary one. Only the repeatable header and footer render a block more
   * than once in full, so this is null-or-stale for everything else and callers
   * treat it as a hint: check it is still an instance of the selected block
   * before using it.
   */
  clickedInstanceRef: RefObject<HTMLElement | null>;
  /** Handlers to spread onto the preview container. */
  previewHandlers: PreviewHandlers;
  /** Inserts an authored block beside the selection (or at document end). */
  insertBlock: (blockHtml: string, position?: BlockInsertPosition) => void;
  /** Deletes the selected block. */
  deleteSelected: () => void;
  /** Duplicates the selected block. */
  duplicateSelected: () => void;
  /** Moves the selected block one step up or down. */
  moveSelected: (direction: "up" | "down") => void;
  /** Inserts an empty table row above or below the selected row or cell. */
  insertTableRow: (position: "above" | "below") => void;
  /** Removes the selected table row (or the selected cell's row). */
  removeTableRow: () => void;
  /** Inserts an empty table column beside the selected cell. */
  insertTableColumn: (side: "left" | "right") => void;
  /** Removes the selected table cell's column. */
  removeTableColumn: () => void;
  /** Merges the selected table cell with its aligned neighbour. */
  mergeTableCells: (direction: MergeDirection) => void;
  /** Splits the selected merged table cell back into single cells. */
  unmergeTableCells: () => void;
  /** Writes an inline-style property on the selected block. */
  setStyle: (property: StyleProperty, value: string) => void;
  /** Writes an attribute on the selected block. */
  setAttribute: (attribute: string, value: string) => void;
  /** Adds or removes one class on the selected block. */
  setClass: (className: string, enabled: boolean) => void;
  /** Selects a block by node id (breadcrumb navigation), committing any open text session. */
  selectNode: (nodeId: string) => void;
  /** Clears the block selection. */
  clearSelection: () => void;
  /** Replaces the selected repeater's template inner HTML as one edit. */
  replaceTemplate: (innerHtml: string) => void;
  /** Appends a bound column to the selected item table, selecting the column. */
  addTableColumn: (source: string, header: string) => void;
  /** Moves an item table's column one step left or right, selecting it. */
  moveTableColumn: (nodeId: string, direction: "left" | "right") => void;
  /**
   * Commits a table column's dragged width, trading it against the column
   * beside it. The selection is left where it was, since the drag ran on the
   * table's chrome rather than on the column.
   */
  resizeColumn: (nodeId: string, percent: number) => void;
  /** Appends a new text column to the selection's enclosing fp-columns row. */
  addColumnsColumn: () => void;
  /**
   * Commits an fp-columns column's dragged width by its position in the row,
   * trading it against the column beside it.
   */
  resizeColumnsColumn: (nodeId: string, index: number, percent: number) => void;
  /** True when an undo step is available. */
  canUndo: boolean;
  /** True when a redo step is available. */
  canRedo: boolean;
  /** Restores the previous history snapshot (document + selection). */
  undo: () => void;
  /** Re-applies the next undone snapshot (document + selection). */
  redo: () => void;
  /**
   * Replaces the working document with new HTML and clears undo/redo history,
   * without recording an undo entry. Used by conflict recovery when adopting
   * the server's document.
   * @param html - The document HTML to make current
   */
  resetDocument: (html: string) => void;
}

const DRAG_THRESHOLD_PX = 6;

/** How long a session's draft waits after an edit before it re-renders, in ms. */
const DRAFT_RENDER_THROTTLE_MS = 100;

/** The caret a detached session is handed over from without a readable one. */
const DETACHED_CARET: SessionCaret = { markdownOffset: 0, top: 0 };

interface DragSession {
  nodeId: string;
  element: HTMLElement;
  startX: number;
  startY: number;
  /** The dragged block's column at drag start, hit-tested against while it moves. */
  column: HitTestColumn;
  dragging: boolean;
  target: { anchorId: string; before: boolean } | null;
}

/**
 * State hook for the WYSIWYG quote-document editor. Owns the stored HTML
 * string (seeded from the backend document), the selection, the hover state,
 * inline text editing, and drag-reorder. Every edit flows through the pure
 * mutations in documentEditor.ts: parse the current HTML, mutate the source
 * DOM, re-serialize - the returned node id keeps the selection on the edited
 * block even though ids are positional.
 *
 * There is one interaction mode. A single click selects a block and, when the
 * click lands on editable text, opens the inline edit session on it in the same
 * gesture. Reordering hangs off hover instead: the pointer moving over a block
 * publishes it as hovered, the canvas draws a grip in the gutter beside it, and
 * a drag can ONLY start from that grip (startBlockDrag) - a pointerdown on a
 * block's body never moves it.
 *
 * Two structural regimes coexist in one document. Flat content (any block
 * outside a `<template>`) is fully editable. Repeater blocks (fp-lines and
 * friends) render live and are selectable as opaque blocks with whole-block
 * ops only; click resolution (renderedDom.ts) redirects any click inside a
 * stamped instance to the owning repeater, so text editing, style knobs, and
 * the edit surface can never land inside a stamped projection.
 *
 * Inline text editing runs in a TipTap session (InlineTextEditOverlay)
 * seeded from the block's SOURCE markdown: fp-fields are atomic chips, "]"
 * opens the field-insert menu in ANY editable block, and bold/italic, lists,
 * headings and dividers edit as native marks/nodes. A session edits the whole
 * block body, so it is anchored to the rendered BLOCK root rather than the
 * clicked line - anchoring it to the line would leave the block's other lines
 * on screen underneath the surface. The whole session commits on blur/Enter as
 * ONE document edit (one history entry); Escape cancels.
 *
 * While a session is open its unsaved draft (textEditDraft.ts) is spliced into
 * `renderedHtml` only - throttled behind the typing - so the preview
 * repaginates live and a new page appears as the text outgrows its page, while
 * `documentHtml`, history and autosave see nothing until the commit. When the
 * draft moves the caret's text to another page's fragment, the session is
 * handed over to that fragment in place (handOverTextEdit), and the one commit
 * still replaces the whole span the session covered.
 *
 * Authored (source-less) fp-markdown blocks are a legacy format: the initial
 * HTML is normalized on load (normalizeAuthoredMarkdownBlocks), converting
 * each one into a plain-HTML prose div so ALL authored prose shares the same
 * inline editing path. Bound fp-markdown blocks stay bound; converting one to
 * editable content also goes through markdown-to-HTML conversion.
 *
 * Undo/redo is snapshot-based (history.ts): every committed mutation records
 * the pre-edit document + selection, style-knob runs coalesce into single
 * entries, and Cmd/Ctrl+Z / Shift+Cmd/Ctrl+Z / Ctrl+Y step the history
 * whenever focus is not inside a text input or an active inline text edit.
 * @param quote - The public quote projection bound into the document; field
 * chips and markdown conversions resolve against it
 * @param initialHtml - The stored document HTML seeding the editor state
 * @param locked - When true, keyboard edit shortcuts are ignored, no drag
 * handle is offered, and no text session can open (an upload is in flight)
 * @returns The editor API consumed by QuoteDocumentTab
 */
export function useDocumentEditor(
  quote: PublicQuote,
  initialHtml: string,
  locked: boolean,
): DocumentEditorApi {
  const [documentHtml, setDocumentHtml] = useState(() =>
    normalizeAuthoredMarkdownBlocks(initialHtml),
  );
  const [renderedHtml, setRenderedHtml] = useState(documentHtml);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropIndicator, setDropIndicator] = useState<DropIndicatorRect | null>(
    null,
  );
  const [textEdit, setTextEdit] = useState<InlineTextEditSession | null>(null);
  const [activeTextEditor, setActiveTextEditor] = useState<Editor | null>(null);
  const [history, setHistory] = useState<HistoryState>(createHistory);

  const previewRef = useRef<HTMLDivElement>(null);
  const clickedInstanceRef = useRef<HTMLElement | null>(null);
  const hoveredInstanceRef = useRef<HTMLElement | null>(null);
  const quoteRef = useRef(quote);
  quoteRef.current = quote;
  const documentHtmlRef = useRef(documentHtml);
  const debounceRef = useRef<number | null>(null);
  const textEditRef = useRef<InlineTextEditSession | null>(null);
  const textEditControlsRef = useRef<InlineTextEditControls | null>(null);
  const textDraftRef = useRef<TextEditDraft | null>(null);
  const draftTimerRef = useRef<number | null>(null);
  const draftVersionRef = useRef(0);
  const pendingDraftVersionRef = useRef(0);
  const renderedDraftVersionRef = useRef(0);
  const sessionCounterRef = useRef(0);
  const dragRef = useRef<DragSession | null>(null);
  const historyRef = useRef(history);
  historyRef.current = history;
  const selectionSnapshotRef = useRef<HistorySelection>({ nodeId: null });
  selectionSnapshotRef.current = { nodeId: selectedNodeId };
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  const selection = useMemo(
    () => (selectedNodeId ? getBlockInfo(documentHtml, selectedNodeId) : null),
    [documentHtml, selectedNodeId],
  );

  const hoverChromeNodeId = useMemo(
    () =>
      hoveredNodeId ? chromeAnchorNodeId(documentHtml, hoveredNodeId) : null,
    [documentHtml, hoveredNodeId],
  );

  const hoverOutlinesSingleInstance = useMemo(
    () =>
      hoveredNodeId
        ? (getBlockInfo(documentHtml, hoveredNodeId)?.repeatsOnEveryPage ??
          false)
        : false,
    [documentHtml, hoveredNodeId],
  );

  /**
   * The HTML to render for a stored document: with the open session's draft
   * spliced in when it holds edits, else the stored HTML itself. Records the
   * draft version it rendered, which the hand-over check waits on.
   * @param storedHtml - The stored document HTML
   * @returns The HTML for the preview
   */
  const renderDraftOver = useCallback((storedHtml: string): string => {
    pendingDraftVersionRef.current = draftVersionRef.current;
    const session = textEditRef.current;
    const draft = textDraftRef.current;
    const editorMarkdown = draft
      ? changedEditorMarkdown(draft, textEditControlsRef.current)
      : null;
    if (!session || !draft || editorMarkdown === null) {
      return storedHtml;
    }
    return (
      commitInlineTextEdit(
        storedHtml,
        session.element,
        foldEditorMarkdown(draft, previewMarkdown(editorMarkdown)),
        draft.base,
      )?.html ?? storedHtml
    );
  }, []);

  const applyEdit = useCallback(
    (edit: DocumentEdit | null, coalesce?: EditCoalescing) => {
      if (!edit) {
        return;
      }
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (edit.html !== documentHtmlRef.current) {
        const snapshot: HistorySnapshot = {
          html: documentHtmlRef.current,
          selection: selectionSnapshotRef.current,
        };
        const now = Date.now();
        setHistory((state) => recordEdit(state, snapshot, now, coalesce));
      }
      documentHtmlRef.current = edit.html;
      setDocumentHtml(edit.html);
      setRenderedHtml(renderDraftOver(edit.html));
      setSelectedNodeId(edit.nodeId);
    },
    [renderDraftOver],
  );

  const restoreSnapshot = useCallback(
    (snapshot: HistorySnapshot) => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      documentHtmlRef.current = snapshot.html;
      setDocumentHtml(snapshot.html);
      setRenderedHtml(renderDraftOver(snapshot.html));
      const block = snapshot.selection.nodeId
        ? getBlockInfo(snapshot.html, snapshot.selection.nodeId)
        : null;
      setSelectedNodeId(block?.nodeId ?? null);
    },
    [renderDraftOver],
  );

  const resetDocument = useCallback((html: string) => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    documentHtmlRef.current = html;
    setDocumentHtml(html);
    setRenderedHtml(html);
    setSelectedNodeId(null);
    setHistory(createHistory());
  }, []);

  /**
   * Ends the open session: commits its whole draft span as ONE document edit,
   * or discards it, restoring the preview to the stored document.
   * @param commit - True to commit the draft, false to discard it
   */
  const finishTextEdit = useCallback(
    (commit: boolean) => {
      const session = textEditRef.current;
      const draft = textDraftRef.current;
      if (!session || !draft) {
        return;
      }
      const editorMarkdown = commit
        ? changedEditorMarkdown(draft, textEditControlsRef.current)
        : null;
      textEditRef.current = null;
      textEditControlsRef.current = null;
      textDraftRef.current = null;
      if (draftTimerRef.current !== null) {
        clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
      }
      setTextEdit(null);
      if (editorMarkdown === null) {
        setRenderedHtml(documentHtmlRef.current);
        return;
      }
      const edit = commitInlineTextEdit(
        documentHtmlRef.current,
        session.element,
        committedSpanMarkdown(draft, editorMarkdown),
        draft.base,
      );
      clickedInstanceRef.current = enclosingInstance(
        session.element,
        edit?.nodeId ?? null,
      );
      if (edit) {
        applyEdit(edit);
        return;
      }
      setRenderedHtml(documentHtmlRef.current);
    },
    [applyEdit],
  );

  /**
   * Installs the open session anchored to a (possibly new) rendered element.
   * Flushed synchronously, so no frame paints the new element unhidden beside a
   * surface still pinned to the old one.
   * @param next - The session to install
   */
  const anchorTextEdit = useCallback((next: InlineTextEditSession) => {
    textEditRef.current = next;
    clickedInstanceRef.current = next.element;
    flushSync(() => setTextEdit(next));
  }, []);

  /**
   * Re-anchors the open session to a rendered fragment: installs the rebased
   * draft and re-seeds the editor with the fragment's slice, caret at the given
   * draft-body offset.
   * @param target - The fragment element to anchor to
   * @param next - The draft rebased onto that fragment
   * @param caretOffset - The caret's offset into the draft body
   */
  const applyHandOver = useCallback(
    (target: HTMLElement, next: TextEditDraft, caretOffset: number) => {
      const session = textEditRef.current;
      if (!session) {
        return;
      }
      const seed = viewMarkdown(next);
      const resolveField = (fieldSource: string) =>
        resolveQuoteField(quoteRef.current, fieldSource);
      const handedOver: InlineTextEditSession = {
        ...session,
        element: target,
        initialDoc: markdownToEditorDoc(seed, resolveField),
        initialSelection: null,
        initialCaret: caretTextblockPoint(
          seed,
          caretOffset - editorBodyWindow(next, seed).start,
          resolveField,
        ),
      };
      textDraftRef.current = next;
      anchorTextEdit(handedOver);
    },
    [anchorTextEdit],
  );

  /**
   * Hands the open session to the fragment that now holds its caret, once the
   * preview shows the editor's latest draft. A hand-over folds the editor's
   * content into the draft span, widens the span over the new fragment, and
   * re-seeds the same session there with the caret at the same text - forward
   * when the text grew onto a later page or pulled units up into this one,
   * backward when deleting emptied a page. It is held off mid-composition,
   * while a toolbar control holds the session and while the field menu is
   * open, and it never fires twice on one rendered draft: the new fragment's
   * window then matches the editor. A fragment re-rendered as a new element
   * that still shows exactly the editor's text is only re-anchored, never
   * re-seeded, so the caret, undo stack and a trailing space all survive. A
   * just-typed trailing space defers a re-seed - it would trim the space -
   * unless the anchored element is gone. A detached element is left even
   * without a readable caret (a node selection), from the view's start.
   */
  const handOverTextEdit = useCallback(() => {
    const session = textEditRef.current;
    const draft = textDraftRef.current;
    const controls = textEditControlsRef.current;
    const container = previewRef.current;
    if (
      !session ||
      !draft ||
      !controls ||
      !container ||
      renderedDraftVersionRef.current !== draftVersionRef.current ||
      !controls.canHandOver() ||
      (session.element.isConnected && controls.caretAfterTrailingSpace())
    ) {
      return;
    }
    const caret =
      controls.readCaret() ??
      (session.element.isConnected ? null : DETACHED_CARET);
    if (!caret) {
      return;
    }
    const changedMarkdown = changedEditorMarkdown(draft, controls);
    const editorMarkdown =
      changedMarkdown === null
        ? viewMarkdown(draft)
        : previewMarkdown(changedMarkdown);
    const view = editorBodyWindow(draft, editorMarkdown);
    const caretOffset = view.start + caret.markdownOffset;
    const target = handOverTarget(
      container,
      session.element,
      caretOffset,
      caret.top,
    );
    if (!target) {
      return;
    }
    const fragment = renderedTextOffsets(target) ?? {
      start: 0,
      end: draftBodyLength(draft, editorMarkdown),
    };
    if (windowShowsEditor(fragment, draft, editorMarkdown)) {
      if (target !== session.element) {
        anchorTextEdit({ ...session, element: target });
      }
      return;
    }
    applyHandOver(
      target,
      rebaseTextEditDraft(
        draft,
        editorMarkdown,
        fragment,
        changedMarkdown !== null,
      ),
      caretOffset,
    );
  }, [anchorTextEdit, applyHandOver]);

  /**
   * Renders the open session's draft into the preview, ending the throttle.
   * The render is flushed synchronously - the paginator re-measures it in the
   * same flush - and the hand-over check runs right after, in the same task,
   * so no keystroke can land in between and leave the check comparing the
   * editor against a stale render. Without that, holding Backspace starved
   * every check and left the session pinned to a fragment that was gone.
   */
  const renderDraft = useCallback(() => {
    draftTimerRef.current = null;
    if (!textEditRef.current) {
      return;
    }
    flushSync(() => setRenderedHtml(renderDraftOver(documentHtmlRef.current)));
    renderedDraftVersionRef.current = pendingDraftVersionRef.current;
    handOverTextEdit();
  }, [renderDraftOver, handOverTextEdit]);

  /** Counts an editor change and schedules the draft render behind it. */
  const handleTextDraftChange = useCallback(() => {
    draftVersionRef.current += 1;
    if (draftTimerRef.current === null) {
      draftTimerRef.current = window.setTimeout(
        renderDraft,
        DRAFT_RENDER_THROTTLE_MS,
      );
    }
  }, [renderDraft]);

  /**
   * Backspace in an emptied editor: drops its empty line and steps the session
   * back to the end of the block's previous fragment, the way Backspace crosses
   * a page break in a word processor. The previous fragment's window is read
   * off the current render, which is safe even if that render lags the editor:
   * nothing before the editor's view changes until a hand-over.
   * @returns True when the session stepped back, false when nothing precedes it
   */
  const stepBackFromEmptyTextEdit = useCallback((): boolean => {
    const session = textEditRef.current;
    const draft = textDraftRef.current;
    const container = previewRef.current;
    if (!session || !draft || !container) {
      return false;
    }
    const viewStart = editorBodyWindow(draft, "").start;
    const nodeId = session.element.getAttribute("data-fp-node");
    const earlier = (
      nodeId ? findRenderedBlockInstances(container, nodeId) : []
    ).filter((instance) => {
      const offsets = renderedTextOffsets(instance);
      return offsets !== null && offsets.end <= viewStart;
    });
    const target = fragmentHoldingOffset(earlier, viewStart);
    const fragment = target ? renderedTextOffsets(target) : null;
    if (!target || !fragment) {
      return false;
    }
    applyHandOver(
      target,
      rebaseTextEditDraft(draft, "", fragment, true),
      fragment.end,
    );
    handleTextDraftChange();
    return true;
  }, [applyHandOver, handleTextDraftChange]);

  const registerTextEditControls = useCallback(
    (controls: InlineTextEditControls | null) => {
      textEditControlsRef.current = controls;
      setActiveTextEditor(controls?.editor ?? null);
    },
    [],
  );

  const beginTextEdit = useCallback((target: Element): boolean => {
    if (textEditRef.current) {
      return false;
    }
    const html = documentHtmlRef.current;
    const editable = findTextEditableElement(target, previewRef.current, html);
    if (!editable) {
      return false;
    }
    const source = resolveSourceEditTarget(html, editable);
    const blockRoot = editable.closest("[data-fp-node]");
    if (!source || !(blockRoot instanceof HTMLElement)) {
      return false;
    }
    setSelectedNodeId(blockRoot.getAttribute("data-fp-node"));
    const textRange = readSourceTextRange(source, editable);
    const session: InlineTextEditSession = {
      id: ++sessionCounterRef.current,
      element: blockRoot,
      initialDoc: loadSessionEditorDoc(source, textRange, (fieldSource) =>
        resolveQuoteField(quoteRef.current, fieldSource),
      ),
      blocksEnabled: blockRoot.tagName !== "SPAN",
      initialSelection: captureSelectionPoints(blockRoot),
      initialCaret: null,
    };
    textDraftRef.current = openTextEditDraft(
      textRange ? authoredBody(source) : "",
      textRange,
      sessionMarkdown(source, textRange),
    );
    draftVersionRef.current = 0;
    pendingDraftVersionRef.current = 0;
    renderedDraftVersionRef.current = 0;
    textEditRef.current = session;
    setTextEdit(session);
    return true;
  }, []);

  const cancelDrag = useCallback(() => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    dragRef.current = null;
    drag.element.style.opacity = "";
    setIsDragging(false);
    setDropIndicator(null);
  }, []);

  const performUndo = useCallback(() => {
    cancelDrag();
    const step = undoHistory(historyRef.current, {
      html: documentHtmlRef.current,
      selection: selectionSnapshotRef.current,
    });
    if (!step) {
      return;
    }
    setHistory(step.state);
    restoreSnapshot(step.snapshot);
  }, [cancelDrag, restoreSnapshot]);

  const performRedo = useCallback(() => {
    cancelDrag();
    const step = redoHistory(historyRef.current, {
      html: documentHtmlRef.current,
      selection: selectionSnapshotRef.current,
    });
    if (!step) {
      return;
    }
    setHistory(step.state);
    restoreSnapshot(step.snapshot);
  }, [cancelDrag, restoreSnapshot]);

  const updateDropTarget = (clientX: number, clientY: number) => {
    const drag = dragRef.current;
    const container = previewRef.current;
    if (!drag || !container) {
      return;
    }
    drag.element.style.pointerEvents = "none";
    const hit = document.elementFromPoint(
      dropHitTestX(clientX, drag.column),
      clientY,
    );
    drag.element.style.pointerEvents = "";
    const candidate = renderedBlockFromEventTarget(hit);
    const hitId = renderedBlockNodeId(candidate);
    if (!candidate || !hitId || !container.contains(candidate)) {
      drag.target = null;
      setDropIndicator(null);
      return;
    }
    const anchorId = chromeAnchorNodeId(documentHtmlRef.current, hitId);
    const anchorElement = dropAnchorElement(container, anchorId, candidate);
    if (!anchorElement || anchorId === drag.nodeId) {
      drag.target = null;
      setDropIndicator(null);
      return;
    }
    const rect = anchorElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const before = clientY < rect.top + rect.height / 2;
    drag.target = { anchorId, before };
    setDropIndicator({
      top:
        (before ? rect.top : rect.bottom) -
        containerRect.top +
        container.scrollTop,
      left: rect.left - containerRect.left + container.scrollLeft,
      width: rect.width,
    });
  };

  const selectFromTarget = (target: Element) => {
    const block = renderedBlockFromEventTarget(target);
    clickedInstanceRef.current = block;
    setSelectedNodeId(renderedBlockNodeId(block));
  };

  const clearHover = () => {
    hoveredInstanceRef.current = null;
    setHoveredNodeId(null);
  };

  const updateHover = (target: EventTarget | null) => {
    if (isEditorChromeTarget(target)) {
      return;
    }
    if (locked) {
      clearHover();
      return;
    }
    const block = renderedBlockFromEventTarget(target);
    hoveredInstanceRef.current = block;
    setHoveredNodeId(renderedBlockNodeId(block));
  };

  const startBlockDrag = (
    nodeId: string,
    event: ReactPointerEvent<HTMLElement>,
  ) => {
    const container = previewRef.current;
    if (locked || event.button !== 0 || !container) {
      return;
    }
    // Hover chrome stays live during a text session, so a grab on another
    // block's handle must land first try: commit the session, then drag.
    if (textEditRef.current) {
      finishTextEdit(true);
    }
    const element = resolveDragElement(
      container,
      nodeId,
      hoveredInstanceRef.current,
    );
    if (!element) {
      return;
    }
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    clickedInstanceRef.current = element;
    dragRef.current = {
      nodeId,
      element,
      startX: event.clientX,
      startY: event.clientY,
      column: element.getBoundingClientRect(),
      dragging: false,
      target: null,
    };
    setSelectedNodeId(nodeId);
  };

  const previewHandlers: PreviewHandlers = {
    onClick: (event) => {
      if (textEditRef.current || isEditorChromeTarget(event.target)) {
        return;
      }
      if (!(event.target instanceof Element)) {
        return;
      }
      selectFromTarget(event.target);
      if (locked) {
        return;
      }
      beginTextEdit(event.target);
    },
    onPointerMove: (event) => {
      const drag = dragRef.current;
      if (!drag) {
        updateHover(event.target);
        return;
      }
      if (!drag.dragging) {
        const distance = Math.hypot(
          event.clientX - drag.startX,
          event.clientY - drag.startY,
        );
        if (distance < DRAG_THRESHOLD_PX) {
          return;
        }
        drag.dragging = true;
        drag.element.style.opacity = "0.4";
        setIsDragging(true);
      }
      event.preventDefault();
      updateDropTarget(event.clientX, event.clientY);
    },
    onPointerUp: () => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }
      dragRef.current = null;
      drag.element.style.opacity = "";
      setIsDragging(false);
      setDropIndicator(null);
      if (drag.dragging && drag.target) {
        applyEdit(
          moveBlockRelative(
            documentHtmlRef.current,
            drag.nodeId,
            drag.target.anchorId,
            drag.target.before ? "before" : "after",
          ),
        );
      }
    },
    onPointerCancel: cancelDrag,
    onPointerLeave: () => {
      if (dragRef.current) {
        return;
      }
      clearHover();
    },
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (lockedRef.current || textEditRef.current || isFormElementFocused()) {
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) {
          performRedo();
          return;
        }
        performUndo();
        return;
      }
      if (event.ctrlKey && !event.metaKey && event.key.toLowerCase() === "y") {
        event.preventDefault();
        performRedo();
        return;
      }
      if (event.key === "Escape") {
        setSelectedNodeId(null);
        return;
      }
      if (
        (event.key === "Delete" || event.key === "Backspace") &&
        selectedNodeId
      ) {
        event.preventDefault();
        applyEdit(removeBlock(documentHtmlRef.current, selectedNodeId));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [applyEdit, performRedo, performUndo, selectedNodeId]);

  // A draft re-rendered outside renderDraft (a toolbar edit or undo while the
  // session is open) is checked for a hand-over a frame after it commits: the
  // paginator re-measures it in a layout effect whose re-render lands only
  // after this commit's passive effects, so the new fragments exist by then.
  useEffect(() => {
    renderedDraftVersionRef.current = pendingDraftVersionRef.current;
    if (!textEditRef.current) {
      return;
    }
    const frame = requestAnimationFrame(handOverTextEdit);
    return () => cancelAnimationFrame(frame);
  }, [renderedHtml, handOverTextEdit]);

  useEffect(
    () => () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
      if (draftTimerRef.current !== null) {
        clearTimeout(draftTimerRef.current);
      }
    },
    [],
  );

  return {
    documentHtml,
    quote,
    renderedHtml,
    hoveredNodeId: locked ? null : hoveredNodeId,
    hoverChromeNodeId: locked ? null : hoverChromeNodeId,
    hoverOutlinesSingleInstance,
    hoveredInstanceRef,
    isDragging,
    startBlockDrag,
    selection,
    isTextEditing: textEdit !== null,
    textEdit,
    activeTextEditor,
    registerTextEditControls,
    commitTextEdit: () => finishTextEdit(true),
    cancelTextEdit: () => finishTextEdit(false),
    onTextDraftChange: handleTextDraftChange,
    onTextElementDetached: handOverTextEdit,
    onTextBackspaceWhenEmpty: stepBackFromEmptyTextEdit,
    convertSelectedMarkdownToAuthored: () => {
      if (!selection || selection.fieldSource === null) {
        return;
      }
      applyEdit(
        convertBoundMarkdownToHtml(
          documentHtmlRef.current,
          selection.nodeId,
          resolveQuoteField(quoteRef.current, selection.fieldSource) ?? "",
        ),
      );
    },
    dropIndicator,
    previewRef,
    clickedInstanceRef,
    previewHandlers,
    insertBlock: (blockHtml, position) =>
      applyEdit(
        insertBlockAt(
          documentHtmlRef.current,
          selectedNodeId,
          blockHtml,
          position,
        ),
      ),
    deleteSelected: () => {
      if (selectedNodeId) {
        applyEdit(removeBlock(documentHtmlRef.current, selectedNodeId));
      }
    },
    duplicateSelected: () => {
      if (selectedNodeId) {
        applyEdit(duplicateBlock(documentHtmlRef.current, selectedNodeId));
      }
    },
    moveSelected: (direction) => {
      if (selectedNodeId) {
        applyEdit(
          moveBlockStep(documentHtmlRef.current, selectedNodeId, direction),
        );
      }
    },
    insertTableRow: (position) => {
      if (selectedNodeId) {
        applyEdit(
          insertTableRow(documentHtmlRef.current, selectedNodeId, position),
        );
      }
    },
    removeTableRow: () => {
      if (selectedNodeId) {
        applyEdit(removeTableRow(documentHtmlRef.current, selectedNodeId));
      }
    },
    insertTableColumn: (side) => {
      if (selectedNodeId) {
        applyEdit(
          insertTableColumn(documentHtmlRef.current, selectedNodeId, side),
        );
      }
    },
    removeTableColumn: () => {
      if (selectedNodeId) {
        applyEdit(removeTableColumn(documentHtmlRef.current, selectedNodeId));
      }
    },
    mergeTableCells: (direction) => {
      if (selectedNodeId) {
        applyEdit(
          mergeTableCells(documentHtmlRef.current, selectedNodeId, direction),
        );
      }
    },
    unmergeTableCells: () => {
      if (selectedNodeId) {
        applyEdit(unmergeTableCells(documentHtmlRef.current, selectedNodeId));
      }
    },
    setStyle: (property, value) => {
      if (selectedNodeId) {
        applyEdit(
          setBlockStyle(
            documentHtmlRef.current,
            selectedNodeId,
            property,
            value,
          ),
          {
            key: `style:${property}:${selectedNodeId}`,
            windowMs: STYLE_COALESCE_WINDOW_MS,
          },
        );
      }
    },
    setAttribute: (attribute, value) => {
      if (selectedNodeId) {
        applyEdit(
          setBlockAttribute(
            documentHtmlRef.current,
            selectedNodeId,
            attribute,
            value,
          ),
        );
      }
    },
    setClass: (className, enabled) => {
      if (selectedNodeId) {
        applyEdit(
          setBlockClass(
            documentHtmlRef.current,
            selectedNodeId,
            className,
            enabled,
          ),
        );
      }
    },
    selectNode: (nodeId) => {
      finishTextEdit(true);
      setSelectedNodeId(nodeId);
    },
    clearSelection: () => setSelectedNodeId(null),
    replaceTemplate: (innerHtml) => {
      if (selectedNodeId) {
        applyEdit(
          replaceTemplateInner(
            documentHtmlRef.current,
            selectedNodeId,
            innerHtml,
          ),
        );
      }
    },
    addTableColumn: (source, header) => {
      if (selectedNodeId) {
        applyEdit(
          appendTableColumn(
            documentHtmlRef.current,
            selectedNodeId,
            source,
            header,
          ),
        );
      }
    },
    moveTableColumn: (nodeId, direction) =>
      applyEdit(moveTableColumn(documentHtmlRef.current, nodeId, direction)),
    resizeColumn: (nodeId, percent) => {
      const edit = resizeTableColumn(documentHtmlRef.current, nodeId, percent);
      if (edit) {
        applyEdit({ html: edit.html, nodeId: selectedNodeId });
      }
    },
    addColumnsColumn: () => {
      if (selectedNodeId) {
        applyEdit(addColumnsColumn(documentHtmlRef.current, selectedNodeId));
      }
    },
    resizeColumnsColumn: (nodeId, index, percent) => {
      const edit = resizeColumnsColumn(
        documentHtmlRef.current,
        nodeId,
        index,
        percent,
      );
      if (edit) {
        applyEdit({ html: edit.html, nodeId: selectedNodeId });
      }
    },
    canUndo: canUndo(history),
    canRedo: canRedo(history),
    undo: performUndo,
    redo: performRedo,
    resetDocument,
  };
}

/**
 * The rendered ancestor-or-self of an element carrying a given node id. A commit
 * reselects the enclosing block, which is a different element from the one the
 * session ran on, so canvas chrome that tracks the clicked copy has to climb
 * with it - it matters for the repeatable header and footer, whose every page
 * holds a copy of that block.
 * @param element - The rendered element the interaction ran on
 * @param nodeId - The node id the interaction resolved to, or null
 * @returns The enclosing rendered instance, or null when none carries the id
 */
function enclosingInstance(
  element: HTMLElement,
  nodeId: string | null,
): HTMLElement | null {
  if (nodeId === null) {
    return null;
  }
  const enclosing = element.closest(`[data-fp-node="${nodeId}"]`);
  return enclosing instanceof HTMLElement ? enclosing : null;
}

/**
 * The open session's editor markdown when the draft holds edits: the editor's
 * content serialized as a slice of the body (every blank line kept) when it
 * changed since its seed, else its seed when an earlier fragment's edits were
 * folded into the draft.
 * @param draft - The session draft
 * @param controls - The mounted overlay's controls, or null
 * @returns The editor's markdown, or null while the session is unchanged
 */
function changedEditorMarkdown(
  draft: TextEditDraft,
  controls: InlineTextEditControls | null,
): string | null {
  const doc = controls?.getDocIfChanged() ?? null;
  if (doc) {
    return editorDocToSliceMarkdown(doc);
  }
  return draft.changed ? seededEditorMarkdown(draft) : null;
}

/**
 * The rendered element a session should be anchored to for its caret: among a
 * paginated block's fragments, the one whose source window holds the caret
 * offset; otherwise the instance whose page window shows the caret line,
 * preferring the current one (a repeated header shows it on every page).
 * @param container - The preview container the instances are looked up in
 * @param element - The element the session is anchored to now
 * @param caretOffset - The caret's offset into the draft body
 * @param caretTop - The caret line's middle, relative to the block's top
 * @returns The element to anchor to, or null when the block is not rendered
 */
function handOverTarget(
  container: HTMLElement,
  element: HTMLElement,
  caretOffset: number,
  caretTop: number,
): HTMLElement | null {
  const nodeId = element.getAttribute("data-fp-node");
  const instances = nodeId ? findRenderedBlockInstances(container, nodeId) : [];
  const fragments = instances.filter(
    (instance) => renderedTextOffsets(instance) !== null,
  );
  if (fragments.length > 0) {
    return fragmentHoldingOffset(fragments, caretOffset);
  }
  if (element.isConnected && showsCaretLine(container, element, caretTop)) {
    return element;
  }
  return (
    instances.find((instance) =>
      showsCaretLine(container, instance, caretTop),
    ) ?? (element.isConnected ? element : (instances[0] ?? null))
  );
}

/**
 * Whether a rendered instance's page window shows the caret line, for a block
 * rendered whole on every page it spans and clipped to each page's band.
 * @param container - The preview container bounding the clip walk
 * @param instance - The rendered block instance
 * @param caretTop - The caret line's middle, relative to the block's top
 * @returns True when nothing clips the instance or its window holds the line
 */
function showsCaretLine(
  container: HTMLElement,
  instance: HTMLElement,
  caretTop: number,
): boolean {
  const bounds = clipAncestorBounds(container, instance);
  const line = instance.getBoundingClientRect().top + caretTop;
  return !bounds || (line >= bounds.top && line < bounds.bottom);
}

/**
 * The rendered element a handle drag should carry: the instance the pointer is
 * hovering when it still belongs to the block, otherwise the block's first
 * rendered instance. The hover hint goes stale whenever the document
 * re-renders, so it is only trusted while it is still connected and still
 * traces to the dragged block.
 * @param container - The preview container the instances are looked up in
 * @param nodeId - The dragged block's node id
 * @param hovered - The instance the hover last resolved to, or null
 * @returns The element to drag, or null when the block has no rendered instance
 */
function resolveDragElement(
  container: HTMLElement,
  nodeId: string,
  hovered: HTMLElement | null,
): HTMLElement | null {
  if (
    hovered?.isConnected &&
    renderedBlockNodeId(hovered) === nodeId &&
    container.contains(hovered)
  ) {
    return hovered;
  }
  return findRenderedBlockInstances(container, nodeId)[0] ?? null;
}

/**
 * The rendered element the drop indicator is drawn on: the anchor instance
 * enclosing the hit element, or the anchor's first instance when the hit block
 * climbed to an anchor that does not contain it.
 * @param container - The preview container the instances are looked up in
 * @param anchorId - The drop anchor's node id
 * @param hit - The rendered block the pointer landed on
 * @returns The anchor's rendered element, or null when it has no instance
 */
function dropAnchorElement(
  container: HTMLElement,
  anchorId: string,
  hit: HTMLElement,
): HTMLElement | null {
  const instances = findRenderedBlockInstances(container, anchorId);
  return (
    instances.find((instance) => instance.contains(hit)) ?? instances[0] ?? null
  );
}

/**
 * Finds the inline-text-editable element for a pointer target: the nearest
 * ancestor (up to and including the block root) whose tag can host a text
 * edit AND whose rendered structure mirrors back to source (which excludes
 * all fp-derived content). A hit inside a list climbs to the outermost
 * mirrorable `<ul>`/`<ol>`. The caller anchors the session to the block root
 * this element sits in, since a session edits the whole block body.
 * @param target - The clicked element
 * @param container - The preview container bounding the walk
 * @param html - The current stored document HTML
 * @returns The editable element, or null
 */
function findTextEditableElement(
  target: Element,
  container: HTMLElement | null,
  html: string,
): HTMLElement | null {
  if (isRenderArtifact(target)) {
    return null;
  }
  for (
    let element: Element | null = target;
    element && element !== container;
    element = element.parentElement
  ) {
    if (
      TEXT_EDITABLE_TAGS.has(element.tagName) &&
      element instanceof HTMLElement &&
      canEditRenderedText(html, element)
    ) {
      return expandToListRoot(element, container, html);
    }
    if (element.hasAttribute("data-fp-node")) {
      return null;
    }
  }
  return null;
}

/**
 * Climbs from a list-interior editable (li/ul/ol) to the outermost
 * mirrorable list element within the block, so Tab-indent and Enter-new-item
 * operate on the full list. Non-list editables pass through unchanged.
 * @param editable - The matched editable element
 * @param container - The preview container bounding the walk
 * @param html - The current stored document HTML
 * @returns The list root, or the original editable
 */
function expandToListRoot(
  editable: HTMLElement,
  container: HTMLElement | null,
  html: string,
): HTMLElement {
  if (!["LI", "UL", "OL"].includes(editable.tagName)) {
    return editable;
  }
  let root = editable;
  for (
    let element: Element | null = editable;
    element && element !== container;
    element = element.parentElement
  ) {
    if (
      (element.tagName === "UL" || element.tagName === "OL") &&
      element instanceof HTMLElement &&
      canEditRenderedText(html, element)
    ) {
      root = element;
    }
    if (element.hasAttribute("data-fp-node")) {
      break;
    }
  }
  return root;
}

/**
 * Whether keyboard focus currently sits in a form control or contenteditable
 * region, so global editor shortcuts (Delete/Backspace/Escape) must not fire.
 * @returns True when a text-entry element has focus
 */
function isFormElementFocused(): boolean {
  const active = document.activeElement;
  return (
    active instanceof HTMLElement &&
    (active.tagName === "INPUT" ||
      active.tagName === "TEXTAREA" ||
      active.tagName === "SELECT" ||
      active.isContentEditable)
  );
}
